import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Radio,
  Table,
  TableColumnsType,
} from "antd";

import { useModal } from "@/hooks/modals/useModal";
import { PlusOutlined } from "@ant-design/icons";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useCancelReasonBehavior } from "../hooks/useCancelReasonBehavior";
import { useFormEditable } from "../hooks/useFormEditable";
import { useSourcingFollowBehavior } from "../hooks/useSourcingFollowBehavior";
import type {
  MaterialItem,
  SourcingRequirementApplicationFormFields,
} from "../type";
import { CancelReasonModal } from "./CancelReasonModal";
import styles from "./MaterialFormFields.module.less";
import { SourcingFollowModal } from "./sourcingNodeFollow/SouringFollowModal";
import { defaultProductionMaterialSourcingNodes } from "./sourcingNodeFollow/node.config";

export function ProductionMaterialFormFields() {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const { isStartedEditable } = useFormEditable();

  const { props } = useModal("SOURCING_APPLICATION_20251202");
  const { mode } = props;

  const isStarted = mode === "started";
  const form = Form.useFormInstance<SourcingRequirementApplicationFormFields>();

  const {
    cancelReasonModalOpen,
    sourcingMaterialId,
    handleCancelModalCancel,
    handleCancelModalConfirm,
    handleCancelSourcingChange,
  } = useCancelReasonBehavior(form);

  const {
    sourcingFollowModalOpen,
    currentSourcingMaterialId,
    handleViewSourcingProgress,
    handleCloseSourcingFollowModal,
  } = useSourcingFollowBehavior();

  const { reviewStatus, materialList } = form.getFieldsValue([
    "reviewStatus",
    "materialList",
  ]);

  const showCancelReason = materialList.some(
    (m: MaterialItem) => m.isSourcingCancel && !!m.cancelReason,
  );

  // 展示寻源执行列条件 reviewStatus === 4 已审批通过
  const showSourcingExecution = reviewStatus === "4";

  return (
    <Form.List name="materialList">
      {(fields, { add, remove }) => {
        const columns: TableColumnsType<any> = [
          {
            title: "序号",
            dataIndex: "materialCode",
            width: 80,
            align: "center",
            fixed: "left",
            render: (_, __, index) => {
              const isHover = hoverIndex === index;
              return isHover && isStartedEditable ? (
                <div
                  className={styles.removeIcon}
                  onClick={() => remove(index)}
                >
                  <Trash2 size={20} />
                </div>
              ) : (
                index + 1
              );
            },
          },
          {
            title: "物料编号",
            dataIndex: "materialCode",
            align: "center",
            width: 100,
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "materialCode"]}
                rules={[{ required: true, message: "请输入编号" }]}
                noStyle
              >
                <Input placeholder="请输入" />
              </Form.Item>
            ),
          },
          {
            title: <span className={styles.required}>物料名称</span>,
            dataIndex: "materialName",
            align: "center",
            width: 100,
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "materialName"]}
                rules={[{ required: true, message: "请输入物料名称" }]}
                noStyle
              >
                <Input placeholder="请输入" />
              </Form.Item>
            ),
          },
          {
            title: "年采购数量",
            dataIndex: "count",
            align: "center",
            width: 120,
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "count"]}
                rules={[{ required: true, message: "请输入年采购数量" }]}
                noStyle
              >
                <Input placeholder="请输入" />
              </Form.Item>
            ),
          },
          {
            title: <span className={styles.required}>相关产品</span>,
            dataIndex: "products",
            align: "center",
            width: 100,
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "products"]}
                rules={[{ required: true, message: "请输入相关产品" }]}
                noStyle
              >
                <Input placeholder="请输入" />
              </Form.Item>
            ),
          },
          {
            title: <span className={styles.required}>要求完成日期</span>,
            dataIndex: "completeDate",
            align: "center",
            width: 150,
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "completeDate"]}
                rules={[{ required: true, message: "请输入要求完成日期" }]}
                noStyle
              >
                <DatePicker />
              </Form.Item>
            ),
          },
          {
            title: <span className={styles.required}>寻源原因</span>,
            dataIndex: "sourcingReason",
            align: "center",
            width: 100,
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                noStyle
                name={[field.name, "sourcingReason"]}
                rules={[{ required: true, message: "请输入寻源原因" }]}
              >
                <Input.TextArea rows={1} placeholder="请输入" />
              </Form.Item>
            ),
          },
          {
            title: <span className={styles.required}>寻源要求</span>,
            dataIndex: "sourcingRequirements",
            align: "center",
            width: 100,
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "sourcingRequirements"]}
                rules={[{ required: true, message: "请输入寻源要求" }]}
                noStyle
              >
                <Input.TextArea rows={1} placeholder="请输入" />
              </Form.Item>
            ),
          },
          {
            title: "寻源优先级",
            dataIndex: "sourcingPrioritize",
            align: "center",
            width: 180,
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "sourcingPrioritize"]}
                noStyle
              >
                <Radio.Group
                  options={[
                    { value: 1, label: "紧急" },
                    { value: 2, label: "一般" },
                  ]}
                />
              </Form.Item>
            ),
          },
          {
            title: "取消寻源",
            dataIndex: "isSourcingCancel",
            align: "center",
            width: 100,
            render: (_, field, index) => {
              /**
               * 同时满足以下条件时，允许编辑取消寻源复选框
               * 1. 当前审核状态为“完成”（reviewStatus === "4"）
               * 2. mode 为 started
               * 3. 该物料的取消寻源状态尚未设置
               */
              const isSourcingCancel = form.getFieldValue([
                "materialList",
                field.name,
                "isSourcingCancel",
              ]);
              const isEditable =
                reviewStatus?.toString() === "4" &&
                isStarted &&
                !isSourcingCancel;

              return (
                <>
                  <Form.Item<
                    SourcingRequirementApplicationFormFields["materialList"]
                  >
                    name={[field.name, "isSourcingCancel"]}
                    noStyle
                    valuePropName="checked"
                  >
                    <Checkbox
                      onChange={(e) =>
                        handleCancelSourcingChange(
                          e.target.checked,
                          index,
                          form.getFieldValue([
                            "materialList",
                            field.name,
                            "id",
                          ]),
                        )
                      }
                      disabled={!isEditable}
                    ></Checkbox>
                  </Form.Item>

                  <Form.Item<
                    SourcingRequirementApplicationFormFields["materialList"]
                  >
                    name={[field.name, "id"]}
                    hidden
                  >
                    <Input placeholder="请输入" />
                  </Form.Item>
                </>
              );
            },
          },
        ];

        if (showCancelReason) {
          columns.push({
            title: "取消原因",
            dataIndex: "cancelReason",
            align: "center",
            width: 140,
            render: (_, field, index) => {
              return (
                <Form.Item<
                  SourcingRequirementApplicationFormFields["materialList"]
                >
                  noStyle
                  name={[field.name, "cancelReason"]}
                >
                  <Input.TextArea rows={1} placeholder="请输入" disabled />
                </Form.Item>
              );
            },
          });
        }

        if (showSourcingExecution) {
          columns.push({
            title: "寻源执行",
            dataIndex: "soucingExecution",
            align: "center",
            fixed: "right",
            width: 150,
            render: (_, field, index) => {
              const sourcingMaterialId = form.getFieldValue([
                "materialList",
                field.name,
                "id",
              ]);

              return (
                <Button
                  disabled={false}
                  onClick={() => handleViewSourcingProgress(sourcingMaterialId)}
                  variant="link"
                  color="primary"
                >
                  查看寻源进度
                </Button>
              );
            },
          });
        }

        return (
          <>
            <Table
              bordered
              dataSource={fields}
              pagination={false}
              scroll={{ x: 1200 }}
              onRow={(_, index) => ({
                onMouseEnter: () => setHoverIndex(index!),
                onMouseLeave: () => setHoverIndex(null),
              })}
              columns={columns}
            ></Table>

            <Button
              hidden={!isStartedEditable}
              icon={<PlusOutlined />}
              onClick={add}
              variant="outlined"
              color="primary"
              className={styles.addBtn}
            >
              添加
            </Button>

            <CancelReasonModal
              sourcingMaterialId={sourcingMaterialId}
              open={cancelReasonModalOpen}
              onClose={handleCancelModalCancel}
              onOk={handleCancelModalConfirm}
            />

            <SourcingFollowModal
              currentSourcingMaterialId={currentSourcingMaterialId!}
              defaultSourcingNodes={defaultProductionMaterialSourcingNodes}
              open={sourcingFollowModalOpen}
              onClose={handleCloseSourcingFollowModal}
            />
          </>
        );
      }}
    </Form.List>
  );
}
